import { useEffect, useState } from 'react';
import type { ApiClient } from '../../api';

/**
 * Der PDF-Knopf der Anforderungsliste — für die Gesamtliste UND je Geschoss.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM JE GESCHOSS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Die August-Vorgabe lautete „PDF-Liste je Geschoss mit Zustand und
 * Anmerkung". Gebaut war bis zum 23.09.2026 genau ein Knopf, und der zog
 * alle offenen Anforderungen des gewählten Planungsbereichs — über alle
 * sieben Geschosse. Eine Datei mit zweitausend Zeilen kommt nicht zurück.
 *
 * Der Architekt arbeitet geschossweise; sein Blatt ist G00 und nicht „alles".
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE ZAHL STEHT AM WÄHLER, NICHT ERST IM PDF
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Dasselbe Muster wie beim Bereichswähler daneben: „G00 — 668 von 1.754
 * offen". Wer erst nach dem Erzeugen sieht, dass ein Geschoss leer ist, hat
 * ein Dokument erzeugt und eine Registernummer verbraucht.
 *
 * Geschosse ohne offene Anforderung stehen deshalb gar nicht erst zur Wahl.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ER HOLT SEINE LISTE SELBST
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Wie <c>AussenBerichtKnopf</c>: Ein Knopf, der dafür eine eigene
 * Ladeverwaltung in der Seite verlangt, wird nicht eingebaut. Scheitert das
 * Laden, bleibt der Knopf ein Knopf für die Gesamtliste — was fehlt, ist die
 * Geschosswahl, nicht die Möglichkeit.
 */
export function AnforderungslisteKnopf({
  api, projektId, bereich, gesamtOffen,
}: {
  api: ApiClient;
  projektId: string;
  /** Der gewählte Planungsbereich, wenn einer gewählt ist. */
  bereich?: string;
  /** Wie viele offen sind — der Knopf bleibt sonst ohne Wirkung. */
  gesamtOffen?: number;
}) {
  const [geschosse, setGeschosse] = useState<
    { geschoss: string | null; stueck: number; ohneAnforderung: number }[]
  >([]);
  const [gewaehlt, setGewaehlt] = useState<string>('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    api.aussparungGeschosseOffen(projektId)
      .then((x) => setGeschosse(x.filter((g) => g.ohneAnforderung > 0)))
      // Ohne Liste bleibt der Knopf ein Knopf für die Gesamtliste.
      .catch(() => setGeschosse([]));
  }, [api, projektId]);

  const hole = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      await api.aussparungAnforderungenPdf(
        projektId, bereich || undefined, gewaehlt || undefined);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  const beschriftung = laeuft
    ? 'erzeugt …'
    : gewaehlt
      ? `als PDF · ${gewaehlt}`
      : 'als PDF für die Architektur';

  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center',
                   flexWrap: 'wrap' }}>
      {geschosse.length > 0 && (
        <select
          value={gewaehlt}
          onChange={(e) => setGewaehlt(e.target.value)}
          aria-label="Geschoss für das PDF"
        >
          <option value="">PDF: alle Geschosse</option>
          {geschosse.map((g) => (
            <option key={g.geschoss ?? ''} value={g.geschoss ?? ''}>
              {g.geschoss ?? 'ohne Geschoss'} — {g.ohneAnforderung} von{' '}
              {g.stueck} offen
            </option>
          ))}
        </select>
      )}

      <button
        type="button"
        className="m-knopf"
        disabled={laeuft || gesamtOffen === 0}
        onClick={hole}
        title={gewaehlt
          ? `Eigenes Blatt für ${gewaehlt} mit eigener Dokumentnummer`
          : 'Ein Blatt über alle Geschosse des gewählten Planungsbereichs'}
      >
        {beschriftung}
      </button>

      {fehler && (
        <span className="hinweis fehler" style={{ fontSize: 11 }}>{fehler}</span>
      )}
    </span>
  );
}
